import { epochOfLocalDateTime, formatMin, localCalendarDate, localMinuteOfDay, type LocalDateTime } from '@wiztime/core';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, ButtonVariant } from '../../../ui/Button';
import { PlannedTimeField } from '../../checkin/PlannedTimeField';
import { doneWindowStart, instantOfWallMinute, wallMinuteInWindow } from './doneTime';
import styles from './DoneTimeEditor.module.css';

/** Quick "N minutes ago" choices of the completion-time editor (checkin-plan-change.md §3.2). */
export const DONE_TIME_QUICK_AGO_MIN = [15, 30, 60] as const;

interface DoneTimeEditorProps {
  readonly itemName: string;
  /** Personal date of the occurrence. */
  readonly date: string;
  readonly dayStartMin: number;
  /** Planned time of the occurrence, or null. */
  readonly plannedMin: number | null;
  /** The occurrence's planned local date-time, or null for an untimed one. */
  readonly occursAt: LocalDateTime | null;
  /** Stored completion instant (ISO), or null when not recorded. */
  readonly doneAt: string | null;
  readonly nowMs: number;
  /** Save the completion instant (ISO) or clear it (null). */
  readonly onSave: (doneAt: string | null) => void;
  readonly onCancel: () => void;
}

/** Epoch ms of an ISO instant, or null when absent or unparsable. */
function msOf(iso: string | null): number | null {
  if (iso === null) return null;
  const ms = Date.parse(iso);
  return Number.isNaN(ms) ? null : ms;
}

/**
 * Editor of a check-in's actual completion time (checkin-plan-change.md §3.2): one-click choices
 * ("as planned", "15 / 30 / 60 minutes ago") save at once; the time field takes a wall time read
 * within 12 hours either side of a timed occurrence, or within the personal day of an untimed one
 * (`doneWindowStart`), and saves with "Save". A time later than now is refused; clearing the field
 * records "time not recorded". A stored instant outside that span cannot be shown as a wall time:
 * it is shown with its date, the field starts empty, and saving without typing keeps it. Enter saves what the field has just committed (read through refs: the field
 * commits and reports Enter within one key event, before this component re-renders).
 */
export function DoneTimeEditor({
  itemName,
  date,
  dayStartMin,
  plannedMin,
  occursAt,
  doneAt,
  nowMs,
  onSave,
  onCancel,
}: DoneTimeEditorProps) {
  const { t } = useTranslation();
  const windowStart = doneWindowStart(occursAt, date, dayStartMin);
  const instantOf = (min: number): number => instantOfWallMinute(windowStart, min);
  const stored = msOf(doneAt);
  const storedMinute = stored === null ? null : wallMinuteInWindow(windowStart, stored);
  const storedInDay = storedMinute !== null;
  const [value, setValue] = useState<number | null>(() => storedMinute);
  const [invalid, setInvalid] = useState(false);
  const valueRef = useRef(value);
  const invalidRef = useRef(false);
  const editedRef = useRef(false);
  const future = value !== null && instantOf(value) > nowMs;

  const quick: { key: string; label: string; ms: number }[] = [];
  const plannedMs = occursAt === null ? null : epochOfLocalDateTime(occursAt);
  if (plannedMin !== null && plannedMs !== null && plannedMs <= nowMs) {
    quick.push({ key: 'planned', label: t('checkin.atPlanned', { time: formatMin(plannedMin) }), ms: plannedMs });
  }
  // "Minutes ago" only while now is within the span the field reads (today, or an overdue dose).
  for (const minutes of DONE_TIME_QUICK_AGO_MIN) {
    const ms = nowMs - minutes * 60_000;
    if (wallMinuteInWindow(windowStart, ms) === null) continue;
    const label = minutes === 60 ? t('checkin.hourAgo') : t('checkin.minutesAgo', { count: minutes });
    quick.push({ key: `ago-${minutes}`, label, ms });
  }

  const save = (): void => {
    const next = valueRef.current;
    if (invalidRef.current || (next !== null && instantOf(next) > nowMs)) return;
    // Nothing typed: keep the stored instant (it may lie outside the personal day).
    if (!editedRef.current) {
      onCancel();
      return;
    }
    onSave(next === null ? null : new Date(instantOf(next)).toISOString());
  };

  return (
    <div className={styles.editor} data-testid="done-time-editor">
      <h3 className={styles.title}>{t('checkin.doneTimeTitle')}</h3>
      {quick.length === 0 ? null : (
        <div className={styles.quick}>
          {quick.map((choice) => (
            <Button
              key={choice.key}
              variant={ButtonVariant.Ghost}
              className={styles.quickButton}
              data-testid={`done-time-${choice.key}`}
              onClick={() => onSave(new Date(choice.ms).toISOString())}
            >
              {choice.label}
            </Button>
          ))}
        </div>
      )}
      {stored !== null && !storedInDay ? (
        <p className={styles.note} data-testid="done-time-outside">
          {t('checkin.doneAtOutside', {
            date: localCalendarDate(stored).slice(5),
            time: formatMin(localMinuteOfDay(stored)),
          })}
        </p>
      ) : null}
      <PlannedTimeField
        value={value}
        itemName={itemName}
        label={t('checkin.doneTimeLabel', { name: itemName })}
        clearLabel={t('checkin.clearTime')}
        testId="done-time-input"
        onCommit={(next) => {
          valueRef.current = next;
          editedRef.current = true;
          setValue(next);
        }}
        onInvalidChange={(next) => {
          invalidRef.current = next;
          setInvalid(next);
        }}
        onEnter={() => setTimeout(save, 0)}
      />
      {future ? (
        <p className={styles.error} role="alert" data-testid="done-time-future">
          {t('checkin.doneTimeFuture')}
        </p>
      ) : null}
      <div className={styles.actions}>
        <Button variant={ButtonVariant.Ghost} data-testid="done-time-cancel" onClick={onCancel}>
          {t('checkin.cancel')}
        </Button>
        <Button variant={ButtonVariant.Primary} data-testid="done-time-save" disabled={invalid || future} onClick={save}>
          {t('checkin.save')}
        </Button>
      </div>
    </div>
  );
}
